/**
 * The signed-in user's access per company (backend utils/accessGrants): in each company, the
 * projects, divisions (SAP Dimension 1) and departments (Dimension 2) they may pick, each every one
 * (`all`) or the listed codes. It comes with the user at login and profile (`user.access`, keyed
 * by company id). Pickers filter by the SELECTED company; Admin is unrestricted. The server checks
 * the same grants, so this only keeps choices that would be refused out of the lists.
 */
export type KindAccess = { all: boolean; codes: string[] };
export type CompanyAccess = { projects: KindAccess; divisions: KindAccess; departments: KindAccess };
export type AccessKind = keyof CompanyAccess;

let signedIn: { isAdmin: boolean; access: Record<string, CompanyAccess> } = { isAdmin: false, access: {} };

/** App.tsx calls this whenever the signed-in user changes. */
export function setSignedInAccess(user: any): void {
  signedIn = {
    isAdmin: String(user?.role || '') === 'Admin',
    access: user && user.access && typeof user.access === 'object' ? user.access : {},
  };
}

export const selectedCompanyId = (): number | undefined => Number(localStorage.getItem('selectedCompanyId') || '') || undefined;

/**
 * The codes of one kind the signed-in user may pick in a company, lower-cased for comparison:
 * null means every one (Admin, or All of that kind in that company).
 */
export function allowedCodes(kind: AccessKind, companyId: number | undefined = selectedCompanyId()): Set<string> | null {
  if (signedIn.isAdmin) return null;
  const access = companyId ? signedIn.access[String(companyId)]?.[kind] : undefined;
  if (access?.all) return null;
  return new Set((access?.codes || []).map((code) => String(code).trim().toLowerCase()));
}

export function isAllowedCode(kind: AccessKind, code: unknown, companyId?: number): boolean {
  const allowed = allowedCodes(kind, companyId);
  return allowed === null || allowed.has(String(code ?? '').trim().toLowerCase());
}
